import libphonenumber, { PhoneNumberFormat } from 'google-libphonenumber'

import Api from './api'
import { MESHIFYID_TOKEN, SIDEWALKID_TOKEN } from './constants'

const phoneUtil = libphonenumber.PhoneNumberUtil.getInstance()

export const saveDeviceIds = () => {
  const urlParams = new URLSearchParams(window.location.search)
  const urlMeshifyId = urlParams.get('mid')
  const urlSidewalkId = urlParams.get('id')
  if (urlMeshifyId && urlSidewalkId) {
    localStorage.setItem(MESHIFYID_TOKEN, urlMeshifyId)
    localStorage.setItem(SIDEWALKID_TOKEN, urlSidewalkId)
  }
}

export const formatAddress = (address: IFolderType['information']['address'] | string, country = '') => {
  let formatted =
    typeof address === 'string'
      ? address
      : [address.address, address.locality, `${address.region} ${address.code}`.trim(), address.country].filter((x) => x).join(', ')

  if (country === 'United States' || country === 'USA' || country === 'US') {
    formatted = formatted
      .replace(' United States ', ' ')
      .replace(' United States', '')
      .replace(' USA ', ' ')
      .replace(' USA', '')
      .replace(' US ', ' ')
      .replace(' US', '')
  }

  // reduce last line to nothing if locality, region, country, code does not exist
  formatted = formatted.replace(/\n(\s*,\s*)/, '\n')
  // reduce multiple new lines to single new line
  formatted = formatted.replace(/\n\n/g, '\n')
  // reduce all multiple spaces to single space
  formatted = formatted.replace(/[ ][ ]+/g, ' ')
  // remove floating comma at the beginning
  formatted = formatted.replace(/^,[ ]+/g, '')
  // remove all extra white space at the beginning/end
  formatted = formatted.trim()
  // trim extra comma at the end if locality exists but not region, country, code
  formatted = formatted.replace(/,$/, '')

  return formatted
}

// Use this method when displaying a phone number
// The phone number should include the country code; if it doesn't, we'll try to assume the country code is +1 (US)
// If the number is still not valid, return the same number back
export const formatPhoneNumber = (phoneNumber: string) => {
  try {
    const number = phoneUtil.parse(phoneNumber)
    const countryCode = number.getCountryCode()
    // Format US numbers as (xxx) xxx-xxxx
    if (countryCode === 1) {
      return phoneUtil.format(number, PhoneNumberFormat.NATIONAL)
    }
    // Otherwise format as an international number as +xx xxx xxx xxxx
    return phoneUtil.format(number, PhoneNumberFormat.INTERNATIONAL)
  } catch (e) {
    // If there is no country code, assume it's US and format as (xxx) xxx-xxxx
    if ((e as { message: string }).message.includes('Invalid country calling code')) {
      try {
        return phoneUtil.format(phoneUtil.parse(`+1 ${phoneNumber}`), PhoneNumberFormat.NATIONAL)
      } catch {
        //
      }
    }
    return phoneNumber
  }
}

// Use this method when saving a phone number to the API
// The phone number should include the country code (provided by Twilio)
// This API call both validates that this is a valid number and that the number is in the right format
export const formatIntlPhoneNumber = async (phoneNumber: string) => {
  const cleanNumber = phoneNumber.replace(/[^+\d]/g, '') // Remove everything except digits and optional + to avoid the endpoint's max character limit of 15
  const res = await Api.get(`/api/users/phoneinfo?number=${cleanNumber}`)
  return res.body.phone_number
}

export const isValidPhoneNumber = (phone: string | undefined) => {
  const phoneNumber = phone ?? ''.replace(/[^+\d]/g, '')
  try {
    return phoneUtil.isValidNumberForRegion(phoneUtil.parse(phoneNumber, 'US'), 'US') || phoneUtil.isValidNumberForRegion(phoneUtil.parse(phoneNumber), 'CA')
  } catch {
    return false
  }
}

export const convertStringToArray = (str: string) => {
  if (str?.trim().length === 0) return []

  let removedBrackets = str
  if (removedBrackets.charAt(0) === '[') removedBrackets = removedBrackets.slice(1)
  if (removedBrackets.charAt(removedBrackets.length - 1) === ']') removedBrackets = removedBrackets.slice(0, -1)
  return removedBrackets.split(',').map((item) => {
    let newItem = item.trim()
    if (['"', "'"].includes(newItem.charAt(0))) newItem = newItem.slice(1)
    if (['"', "'"].includes(newItem.charAt(newItem.length - 1))) newItem = newItem.slice(0, -1)
    return newItem.trim()
  })
}

export const parseAlertName = (alarmName: string) => {
  const index = alarmName.lastIndexOf('|')
  return index !== -1 ? alarmName.slice(0, index).trim() : alarmName
}

export const formatAlerts = (apiData: IAlertHistory[]): IAlertHistory[] => {
  return apiData.map((alert) => ({
    ...alert,
    name: parseAlertName(alert.name),
  }))
}

export const fixQueryString = (pathname: string) => {
  // Fix for query in pathname, for example: /id=995ccc9&mid=EAAAAFX&sku=MYLEAKS5&model=LDMX001
  // Check if the pathname is a query
  let queryString = ''
  if (/^\/[^/]+=[^/&]+(&[^/]+=[^/&]+)*$/.test(pathname)) {
    // Convert the pathname to a query string format
    queryString = pathname.replace(/^\/(.+)/, '?$1')
  }
  return queryString
}

export const locationsListArray = (folderTheme: IFolderTheme | undefined) => {
  let locationsList: string[] = []
  if (folderTheme?.deviceLocations) {
    locationsList =
      typeof folderTheme.deviceLocations === 'string' ? convertStringToArray(folderTheme.deviceLocations) : (folderTheme.deviceLocations as string[])
  }
  return locationsList
}
